import React from 'react';
import { Workflow } from '../../../types/workflow';

const SEVERITY: Record<string, { bg: string; color: string }> = {
  critical: { bg: '#FEE2E2', color: '#b91c1c' },
  high:     { bg: '#FEE2E2', color: '#b91c1c' },
  medium:   { bg: '#FEF3C7', color: '#92400e' },
  low:      { bg: '#E0F2FE', color: '#075985' },
};

function fmtDuration(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 100) / 10;
  return s < 60 ? `${s}s` : `${(s / 60).toFixed(1)}m`;
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white border border-trim rounded-xl p-4">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-xl font-semibold text-ink mt-1">{value}</div>
      {sub && <div className="text-xs text-muted mt-0.5">{sub}</div>}
    </div>
  );
}

export function OverviewTab({ workflow }: { workflow: Workflow }) {
  const k = workflow.kpis;
  return (
    <div className="space-y-6">
      <section className="bg-white border border-trim rounded-xl p-5">
        <h3 className="text-sm font-semibold text-ink mb-2">Business summary</h3>
        <p className="text-sm text-copy leading-relaxed">{workflow.business_summary}</p>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-ink mb-3">Runtime summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Executions / day"   value={k.executions_per_day.toString()} />
          <KpiCard label="Failure rate"        value={`${k.failure_rate.toFixed(1)}%`} />
          <KpiCard label="Avg duration"        value={fmtDuration(k.avg_duration_ms)} />
          <KpiCard label="p95 duration"        value={fmtDuration(k.p95_duration_ms)} />
          <KpiCard label="AI confidence avg"   value={(k.ai_confidence_avg * 100).toFixed(0) + '%'} />
          <KpiCard label="Est cost / run"      value={`$${k.est_cost_per_run_usd.toFixed(3)}`} />
          <KpiCard label="Retry rate"          value={`${k.retry_rate.toFixed(1)}%`} />
          <KpiCard label="Queue depth"         value={k.queue_depth.toString()} />
          <KpiCard label="Human interventions" value={k.human_interventions.toString()} />
          <KpiCard label="Token usage 24h"     value={k.token_usage_24h.toLocaleString()} />
        </div>
      </section>

      <section className="bg-white border border-trim rounded-xl p-5">
        <h3 className="text-sm font-semibold text-ink mb-2">Dependency summary</h3>
        <p className="text-sm text-copy leading-relaxed">{workflow.dependency_summary}</p>
      </section>

      <section className="bg-white border border-trim rounded-xl p-5">
        <h3 className="text-sm font-semibold text-ink mb-3">Recent incidents</h3>
        {workflow.recent_incidents.length === 0 ? (
          <div className="text-sm text-muted">No recent incidents.</div>
        ) : (
          <ul className="space-y-2.5">
            {workflow.recent_incidents.map(i => {
              const sv = SEVERITY[i.severity];
              return (
                <li key={i.id} className="flex items-start gap-3 text-sm">
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0" style={{ background: sv.bg, color: sv.color }}>{i.severity.toUpperCase()}</span>
                  <div className="min-w-0">
                    <div className="text-ink">{i.title} <span className="text-xs text-muted">({i.status})</span></div>
                    <div className="text-xs text-muted mt-0.5">{new Date(i.opened_at).toLocaleString()} — {i.summary}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
